import { useEffect } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { CheckmarkOutline, Upload, Warning } from '@mint-lib/icons';
import { Button, typographyVariants } from '@mint-lib/tw-components';
import { Alert } from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';

import {
  BackButton,
  DiscardButton,
  Switch,
} from '../../../components/atoms/index.js';
import { RichTooltip } from '../../../components/atoms/RichTooltip.jsx';
import { ErrorsFoundLabel } from '../../../components/molecules/ErrorsFoundLabel.jsx';
import { NavbarSlot } from '../../../components/molecules/NavbarSlot.jsx';
import { ValidationProgressState } from '../../../components/molecules/ValidationProgressState.jsx';
import { DataTable } from '../../../components/organisms/DataTable.jsx';
import { useDataFileContext } from '../../../contexts/DataFileContext/useDataFileContext.js';
import { useNativeMetricsPlatformMediaRow } from '../../../hooks/ff/index.js';
import { useDiscard } from '../../../hooks/useDiscard.js';
import { useValidationRouteApi } from '../../../hooks/useImportHubRouteApi.js';
import {
  useDataFileServerStepsConfig,
  useDiscardValidatedRows,
} from '../../../queries/index.js';
import {
  getNativeMetricsBannerDescription,
  getNativeMetricsBannerSeverity,
  getNextButtonTooltip,
  isNextButtonDisabled,
  isNextButtonTooltipDisabled,
} from './nativeMetricsBanner.js';
import { useValidatedData } from './useValidatedData.js';

export const ValidationStep = () => {
  const { t } = useTranslation('@myn/import-hub');
  const validationRoute = useValidationRouteApi();
  const navigate = validationRoute.useNavigate();
  const searchParams = validationRoute.useSearch();

  const {
    dataFileId,
    goToNextStep,
    goToPrevStep,
    isSubmitting,
    showUploadFileDialog,
  } = useDataFileContext();

  const { mutateAsync: discardDataFileRows, isPending: isDiscarding } =
    useDiscardValidatedRows(dataFileId, searchParams);

  const {
    selection,
    selectedRowsIds,
    hasSelection,
    resetSelection,
    handleSelectAllRows,
    handleSelectRow,
    discardRows,
  } = useDiscard({
    dataFileId,
    searchParams: searchParams,
    discardDataFileRows,
  });

  const isNativeMetricsFFEnabled = useNativeMetricsPlatformMediaRow();

  const {
    isValidating,
    validationState,
    dataFilePreview,
    validatedRows,
    errorsCount,
    nativeMetricsBanner,
    hasImportableData,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useValidatedData(dataFileId, searchParams, {
    enabled: !isDiscarding,
  });

  const stepsConfig = useDataFileServerStepsConfig(dataFileId);
  const isLastStep = stepsConfig.isLastStep(stepsConfig.validation.name);

  const headers = validatedRows?.pages[0].additional.headers;
  const rows = validatedRows?.pages.flatMap((page) => page.items) ?? [];
  const hasRows = Boolean(rows.length);
  const hasErrors = Boolean(errorsCount);

  const isValid = !isLoading && !hasErrors && !dataFilePreview.isEdited;

  useEffect(() => {
    if (isValid && searchParams.onlyErrors) {
      navigate({
        search: (prev) => ({ ...prev, onlyErrors: false }),
      });
    }
  }, [isValid, navigate, searchParams.onlyErrors]);

  const handleShowOnlyRowsWithErrorsChange = (checked: boolean) => {
    resetSelection();
    navigate({
      search: (prev) => ({ ...prev, onlyErrors: checked }),
    });
  };

  const validNextButtonText = isLastStep ? t('Finish import') : t('Next');
  const nextButtonText = isValid ? validNextButtonText : t('Validate');

  const actions = (
    <>
      <Button
        onClick={showUploadFileDialog}
        variant="text"
        disabled={isLoading || isValidating || isSubmitting}
        data-dd-action-name="Replace file"
      >
        <Upload />
        {t('Replace file')}
      </Button>
      <BackButton
        onClick={goToPrevStep}
        disabled={isValidating || isSubmitting}
      />
      <RichTooltip
        asChild={true}
        description={getNextButtonTooltip(
          isNativeMetricsFFEnabled,
          hasImportableData,
          t,
        )}
        side="left"
        disabled={isNextButtonTooltipDisabled(hasErrors, hasImportableData)}
      >
        <div>
          <Button
            disabled={isNextButtonDisabled({
              isLoading,
              isDiscarding,
              hasRows,
              hasErrors,
              isSubmitting,
              isFFEnabled: isNativeMetricsFFEnabled,
              hasImportableData,
            })}
            onClick={goToNextStep}
            data-dd-action-name={nextButtonText}
          >
            {nextButtonText}
          </Button>
        </div>
      </RichTooltip>
    </>
  );

  if (isValidating) {
    return (
      <>
        <NavbarSlot>{actions}</NavbarSlot>
        <ValidationProgressState validationState={validationState} />
      </>
    );
  }

  return (
    <>
      <NavbarSlot>{actions}</NavbarSlot>
      <div className="mb-6 flex flex-col">
        <h4 className={cn(typographyVariants({ variant: 'h4' }), 'mb-2')}>
          {t('Data check')}
        </h4>
        <p className={typographyVariants({ variant: 'bodyLong01' })}>
          {t(
            'Review and edit cells if necessary. Discard any unwanted rows, and proceed or return to the upload phase.',
          )}
        </p>
      </div>
      {isNativeMetricsFFEnabled && nativeMetricsBanner && (
        <Alert
          severity={getNativeMetricsBannerSeverity(nativeMetricsBanner)}
          variant="inline"
          description={getNativeMetricsBannerDescription(
            nativeMetricsBanner,
            t,
          )}
          removable={false}
          className="mb-4"
        />
      )}
      <div className="mb-4 flex items-center gap-2">
        <DiscardButton
          onDiscard={discardRows}
          disabled={!hasSelection || isDiscarding}
        />
        <div className="ml-auto flex items-center gap-4">
          {hasErrors ? (
            <ErrorsFoundLabel
              className="text-error-dark"
              message={t('Rows with errors found')}
              icon={Warning}
            />
          ) : (
            !dataFilePreview.isEdited && (
              <ErrorsFoundLabel
                className="text-success-dark"
                message={t('No errors found')}
                icon={CheckmarkOutline}
              />
            )
          )}
          {(hasErrors || searchParams.onlyErrors) && (
            <Switch
              label={t('Show only errors')}
              checked={Boolean(searchParams.onlyErrors)}
              onCheckedChange={handleShowOnlyRowsWithErrorsChange}
              size="small"
            />
          )}
        </div>
      </div>
      <DataTable
        {...{
          dataFileId,
          searchParams,
          data: validatedRows,
          headers,
          rows,
          selection,
          selectedRowsIds,
          handleSelectAllRows,
          handleSelectRow,
          isLoading,
          isFetchingNextPage,
          hasNextPage,
          fetchNextPage,
        }}
      />
    </>
  );
};
